/**
 * Canvas field: gutter rails with ruler ticks, light packets and corner marks.
 * One fixed full-viewport canvas (`#home-field`, rendered by SiteShell) shared by every page.
 *
 * `initField()` returns `null` when the canvas is missing, otherwise a handle whose
 * `destroy()` stops the loop and removes every listener.
 */
export type Field = { setProgress: (p: number) => void; destroy: () => void };

export function initField(staticOnly: boolean): Field | null {
  const canvas = document.getElementById("home-field") as HTMLCanvasElement | null;
  if (!canvas || !canvas.getContext) return null;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const ac = new AbortController();
  const { signal } = ac;
  let dpr = 1;
  let w = 0;
  let h = 0;
  let rails: Array<{ x: number; dir: number }> = [];
  let packets: Array<{ rail: number; y: number; dir: number; speed: number }> = [];
  let running = false;
  let last = 0;
  let raf = 0;
  let resizeTimer = 0;
  const TOP = 76;
  /* Theme-aware colours: read from the CSS tokens, refreshed on theme change. */
  let ov = "235,237,240";
  let sig = "94,200,255";
  let sigHi = "184,232,255";
  const readPalette = () => {
    const cs = getComputedStyle(document.documentElement);
    const light = document.documentElement.dataset.theme === "light";
    ov = cs.getPropertyValue("--ov").trim() || ov;
    sig = cs.getPropertyValue("--signal-rgb").trim() || sig;
    sigHi = light ? sig : "184,232,255";
  };
  readPalette();
  const lineK = () => (document.documentElement.dataset.theme === "light" ? 2.6 : 1);

  function size() {
    w = window.innerWidth;
    h = window.innerHeight;
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas!.width = Math.floor(w * dpr);
    canvas!.height = Math.floor(h * dpr);
    canvas!.style.width = w + "px";
    canvas!.style.height = h + "px";
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    build();
  }

  function build() {
    const bot = h - 20;
    const span = Math.max(1, bot - TOP);
    rails = w < 901 ? [{ x: 10, dir: 1 }] : [{ x: 12, dir: 1 }, { x: w - 8, dir: -1 }];
    packets = [];
    if (!staticOnly) {
      const n = rails.length === 1 ? 2 : 3;
      for (let i = 0; i < n; i++) {
        packets.push({
          rail: i % rails.length,
          y: TOP + span * ((i * 0.31 + 0.18) % 1),
          dir: i % 2 === 0 ? 1 : -1,
          speed: 30 + i * 9,
        });
      }
    }
    draw();
  }

  function corners(inset: number, arm: number) {
    const c = ctx!;
    c.beginPath();
    c.moveTo(inset, inset + arm);
    c.lineTo(inset, inset);
    c.lineTo(inset + arm, inset);
    c.moveTo(w - inset - arm, inset);
    c.lineTo(w - inset, inset);
    c.lineTo(w - inset, inset + arm);
    c.moveTo(inset, h - inset - arm);
    c.lineTo(inset, h - inset);
    c.lineTo(inset + arm, h - inset);
    c.moveTo(w - inset - arm, h - inset);
    c.lineTo(w - inset, h - inset);
    c.lineTo(w - inset, h - inset - arm);
    c.stroke();
  }

  function draw() {
    const c = ctx!;
    c.clearRect(0, 0, w, h);
    const bot = h - 20;

    c.lineWidth = 1;
    c.strokeStyle = `rgba(${ov},${0.16 * lineK()})`;
    corners(10, 14);

    for (const r of rails) {
      const g = c.createLinearGradient(0, TOP, 0, bot);
      g.addColorStop(0, `rgba(${ov},0)`);
      g.addColorStop(0.5, `rgba(${ov},${0.11 * lineK()})`);
      g.addColorStop(1, `rgba(${ov},0)`);
      c.strokeStyle = g;
      c.beginPath();
      c.moveTo(r.x + 0.5, TOP);
      c.lineTo(r.x + 0.5, bot);
      c.stroke();

      c.strokeStyle = `rgba(${ov},${0.1 * lineK()})`;
      c.beginPath();
      let k = 0;
      for (let y = TOP + 12; y < bot - 6; y += 22) {
        const len = k % 5 === 0 ? 8 : 4;
        c.moveTo(r.x, Math.round(y) + 0.5);
        c.lineTo(r.x + r.dir * len, Math.round(y) + 0.5);
        k++;
      }
      c.stroke();
    }

    for (const p of packets) {
      const r = rails[p.rail];
      const tail = p.y - p.dir * 78;
      const g = c.createLinearGradient(0, tail, 0, p.y);
      g.addColorStop(0, `rgba(${sig},0)`);
      g.addColorStop(1, `rgba(${sigHi},0.9)`);
      c.strokeStyle = g;
      c.lineWidth = 1.5;
      c.beginPath();
      c.moveTo(r.x + 0.5, tail);
      c.lineTo(r.x + 0.5, p.y);
      c.stroke();
      const rg = c.createRadialGradient(r.x + 0.5, p.y, 0, r.x + 0.5, p.y, 11);
      rg.addColorStop(0, `rgba(${sigHi},0.5)`);
      rg.addColorStop(1, `rgba(${sigHi},0)`);
      c.fillStyle = rg;
      c.fillRect(r.x - 12, p.y - 12, 24, 24);
    }
  }

  function tick(now: number) {
    if (!running) return;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
    last = now;
    const bot = h - 20;
    for (const p of packets) {
      p.y += p.dir * p.speed * dt;
      if (p.y > bot) {
        p.y = bot;
        p.dir = -1;
      } else if (p.y < TOP) {
        p.y = TOP;
        p.dir = 1;
      }
    }
    draw();
    raf = window.requestAnimationFrame(tick);
  }

  function start() {
    if (staticOnly || running) return;
    running = true;
    last = 0;
    raf = window.requestAnimationFrame(tick);
  }
  function stop() {
    running = false;
    if (raf) window.cancelAnimationFrame(raf);
    raf = 0;
  }

  size();
  window.addEventListener(
    "themechange",
    () => {
      readPalette();
      draw();
    },
    { signal }
  );
  window.addEventListener(
    "resize",
    () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(size, 120);
    },
    { signal }
  );
  document.addEventListener("visibilitychange", () => (document.hidden ? stop() : start()), { signal });
  if (!staticOnly && !document.hidden) start();

  return {
    setProgress: () => {},
    destroy: () => {
      ac.abort();
      window.clearTimeout(resizeTimer);
      stop();
      ctx!.clearRect(0, 0, w, h);
    },
  };
}
